export interface WellknownMixpanelEvents {
  $experiment_started: {
    "Experiment name": string;
    "Variant name": string;
  };
  OnMeristemExperimentDecided: {
    experimentName: string;
    /** @deprecated Use meristemSha instead. */
    sha: string;
    variationName: string;
  };
}

// Const array so we can use this in runtime
export const referralMethods = [
  "sms",
  "email",
  "link",
  "shareApi",
  "buyflow",
  "familyPlan",
] as const;

// Use array above to create a type which we can use to generate proto files
// in noom-contract repo
export type ReferralMethod = Uppercase<typeof referralMethods[number]>;

// NoomBuyflowEvents are sent directly to data lake via kinesis stream.
// Before they end up in data lake, their schema is validated against their
// proto definitions in noom-contracts repo.
//
// To make this process transparent for engineers, buyflow client is the source
// of truth for these events and when this interface is changed and merged to
// master new proto definitions are automatically generated and PR in noom-contracts
// is opened.

/** Base type for all user-triggered events logged by the Buyflow client */
export interface NoomBuyflowEvents {
  BuyflowButtonClicked: {
    buttonName: string;
    pageId: string;
    surveyName: string;
  };
  BuyflowCheckboxClicked: {
    checkboxName: string;
    checked: boolean;
  };
  BuyflowClientInitialized: {
    consent: string[];
    forcedNoomPlanId?: string;
    forcedPlanDurationMonth?: number;
    leadCaptureSinceDays?: number;
    leadCaptureTime?: string;
    navigationType: string;
    newSession: boolean;
    secondsSinceSessionStart: number;
    visibilityState: string;
  };
  BuyflowLinkClicked: {
    linkName: string;
    pageId: string;
    surveyName: string;
  };
  BuyflowPageViewed: {
    pageId: string;
  };
  BuyflowPaymentEltvComputed: {
    eltvValue: number;
  };
  BuyflowPremiumOfferPageViewed: {
    upsellType: string;
  };
  BuyflowQuestionAnswered: {
    /** For questions that store any additional info, i.e. height and weight values, "other" free-text responses, etc. */
    answerParts?: Record<string, string>;
    /** For the main answer(s) to the question. Used in the majority of cases. */
    answers?: string[];
    lastQuestionSeen: string;
    questionId: string;
    surveyName: string;
  };
  BuyflowQuestionViewed: {
    lastQuestionSeen: string;
    questionId: string;
    surveyName: string;
  };
  BuyflowQuizFinished: {
    quizId: string;
    quizScore: number;
  };
  BuyflowSurveyFinished: {
    surveyName: string;
  };
  BuyflowSurveyStarted: {
    surveyName: string;
  };
  CancellationOfferAccepted: {
    cancellationOffers: string[];
  };
  CancellationOfferRejected: {
    cancellationOffers: string[];
  };
  CancellationOfferViewed: {
    cancellationOffers: string[];
  };
  CancellationSurveyFinished: {
    canceled: boolean;
    cancellationOffersAccepted: string[];
    cancellationOffersFailed: string[];
    cancellationOffersRejected: string[];
  };
  CancellationSurveyStarted: {
    subscriptionId: string;
  };
  CheckoutPlanSelectionClicked: {
    planCurrency: string;
    planDurationInMonths: number;
    planId: string;
    planPrice: number;
    planType: string;
    preSelected: boolean;
  };
  CheckoutPlanSelectionViewed: {
    planOptionCount: number;
    planOptionIds: string[];
    planOptionTypes: string[];
  };
  ExperimentStarted: {
    experimentName: string;
    variantName: string;
  };
  /** Generic event to track all experimental events without updating noom-contracts */
  ExperimentalBuyflowEvent: {
    /**
     * This should match the name of the event that would exist if this
     * experimental event was baselined.
     */
    experimentalEventType: string;
  };
  FinalUpdateGraphPageViewed: {
    enhancements: string[];
  };
  PremiumMealPageViewed: {
    dietaryRestrictions: string;
    mealPlanWeek: number;
    pageId: string;
  };
  PremiumMealPlanRecipePageViewed: {
    recipeId: string;
  };
  RefereeSignup: {
    promoCode?: string;
    refereeAccessCode: string;
    refereeGrowthUserId: string;
    referralMethod: ReferralMethod;
  };
  ReferralLinkRedirectPageViewed: {
    isValidReferralCode: boolean;
    queryParams: Record<string, string>;
    referralCode: string;
    shareSource: string;
  };
  ReferralSubmitted: {
    refereeEmailAddress?: string;
    refereePhoneNumber?: string;
    referralEntryPoint: "IN_APP" | "BUYFLOW" | "EMAIL";
    referralMethod: ReferralMethod;
    referrerAccessCode: string;
    referrerGrowthUserId: string;
    talkableCampaignId?: string;
  };
  ShareLinkRedirectPageViewed: {
    queryParams: Record<string, string>;
    shareSource: string;
  };
  ShopifyRedirectCompleted: {
    shopifyUrl: string;
  };
  ShopifyRedirectErrored: {
    errorReason: string;
  };
  ShopifyUrlGenerated: {
    shopifyUrl: string;
    utmCampaign: string;
    utmContent: string;
    utmMedium: string;
    utmSource: string;
    utmTerm: string;
  };
  ShopifyUrlGenerationStarted: {
    slug: string;
    utmCampaign: string;
    utmContent: string;
    utmMedium: string;
    utmSource: string;
    utmTerm: string;
  };
  SignupCompleted: {
    hasTrial: boolean;
    paymentMethod: string;
    planCurrency: string;
    planDurationMonth: number;
    planId: string;
    planOriginalPriceUsd: number;
    planPriceUsd: number;
    program: string;
    subscriptionId: string;
    trialFeeAmount: number;
  };
  SwitchFamilyPlanPageViewed: {
    hasFamilyPlan: boolean;
  };
  TelehealthBuyflowButtonClicked: {
    buttonName: string;
    pageId: string;
    surveyName: string;
  };
  TelehealthBuyflowCheckboxClicked: {
    checkboxName: string;
    checked: boolean;
  };
  TelehealthBuyflowClinicalSchedulingFinished: Record<string, string>;
  TelehealthBuyflowClinicalSchedulingStarted: Record<string, string>;
  TelehealthBuyflowClinicalSurveyFinish: {
    consentedToLabs: boolean;
    consentedToSms: boolean;
    hasLabs: boolean;
    medicalIntakeSkippedOptionalQuestions: boolean;
    pharmacyPreferenceSet: boolean;
    scheduledVisit: boolean;
    skippedInsuranceIntake: boolean;
    uploadedLabs: boolean;
  };
  TelehealthBuyflowClinicalSurveyStart: {
    reason: string;
  };
  TelehealthBuyflowDropdownItemSelected: {
    dropdownName: string;
    selectedItem: string;
    surveyName: string;
  };
  TelehealthBuyflowInsuranceSurveyFinished: {
    skippedInsuranceIntake: boolean;
  };
  TelehealthBuyflowLabsSurveyFinished: {
    consentedToLabs: boolean;
    hasLabs: boolean;
    uploadedLabs: boolean;
  };
  TelehealthBuyflowMedicalSurveyFinished: {
    medicalIntakeSkippedOptionalQuestions: boolean;
  };
  TelehealthBuyflowModalAnswered: {
    answer: string;
    name: string;
  };
  TelehealthBuyflowPharmacySurveyFinished: {
    pharmacyPreferenceSet: boolean;
  };
  TelehealthBuyflowQuestionAnswered: {
    /** For questions that store any additional info, i.e. height and weight values, "other" free-text responses, etc. */
    answerParts?: Record<string, string>;
    /** For the main answer(s) to the question. Used in the majority of cases. */
    answers?: string[];
    lastQuestionSeen?: string;
    questionId: string;
    surveyName: string;
  };
  TelehealthBuyflowQuestionViewed: {
    lastQuestionSeen: string;
    questionId: string;
    surveyName: string;
  };
  TelehealthBuyflowSurveyError: {
    reason: string;
  };
  TelehealthBuyflowSurveyFinished: {
    surveyName: string;
  };
  TelehealthBuyflowSurveyStarted: {
    surveyName: string;
  };
  UserConsentSaved: {
    copy?: string;
    type: string;
  };
  UserPortalAddOnSubscriptionPageViewed: {
    pageId: string;
    subscriptionType: string;
  };
  WeightGainInterested: {
    currentWeight: number;
    idealWeight: number;
  };
}

/** Common properties for events logged by Buyflow client */
export interface NoomBuyflowEventSharedProperties {
  browserFamily: string;
  browserMajorVersion: string;
  buyflowGitSha: string;
  condensedContextType: string;
  consentFramework: string;
  currentUrl: string;
  deviceBrand: string;
  deviceFamily: string;
  deviceIsMobile: boolean;
  fullStorySessionUrl: string;
  geoIpCity: string;
  geoIpContinent: string;
  geoIpCountry: string;
  geoIpSubdivision: string;
  growthUserId: string;
  hasBeenVisible: boolean;
  httpUserAgent: string;
  initialUrl: string;
  isUnloading: boolean;
  isVisible: boolean;
  language: string;
  meristemContextType: string;
  meristemExperiments: string[];
  meristemJourney: Record<string, string>;
  meristemSha: string;
  meristemTreatment: string;
  meristemVariations: string[];
  noomPlanId: string;
  osFamily: string;
  osMajor: string;
  pageSet: string;
  referrerUrl: string;
  routeId: string;
  trafficType: string;
  utmCampaign: string;
  utmContent: string;
  utmMedium: string;
  utmSource: string;
  utmTerm: string;
}

/**
 * Events that does not conform to a proto schema (created from
 * NoomBuyflowEventSharedProperties interface) will not pass the validation
 * in event ingestion lambda.
 * NOOM_BUYFLOW_EVENT_SHARED_PROPERTIES is a duplicated list of the properties from
 * the interface above used to filter the properties sent to data lake. We need
 * this duplication so that we can filter out the invalid properties in runtime
 * and typings ensure that this list will always match the interface above.
 */
export const NOOM_BUYFLOW_EVENT_SHARED_PROPERTIES: Record<
  keyof NoomBuyflowEventSharedProperties,
  true
> = {
  browserFamily: true,
  browserMajorVersion: true,
  buyflowGitSha: true,
  condensedContextType: true,
  consentFramework: true,
  currentUrl: true,
  deviceBrand: true,
  deviceFamily: true,
  deviceIsMobile: true,
  fullStorySessionUrl: true,
  geoIpCity: true,
  geoIpContinent: true,
  geoIpCountry: true,
  geoIpSubdivision: true,
  growthUserId: true,
  hasBeenVisible: true,
  httpUserAgent: true,
  initialUrl: true,
  isUnloading: true,
  isVisible: true,
  language: true,
  meristemContextType: true,
  meristemExperiments: true,
  meristemJourney: true,
  meristemSha: true,
  meristemTreatment: true,
  meristemVariations: true,
  noomPlanId: true,
  osFamily: true,
  osMajor: true,
  pageSet: true,
  referrerUrl: true,
  routeId: true,
  trafficType: true,
  utmCampaign: true,
  utmContent: true,
  utmMedium: true,
  utmSource: true,
  utmTerm: true,
};

export interface NoomBanditEvents {
  BanditFeedbackProvided: {
    banditFeedbackValue: number;
    banditKey: string;
    banditRecommendationUniqueId: string;
  };
  BanditRecommendationProvided: {
    banditKey: string;
    banditRecommendationUniqueId: string;
    recommendedBanditArm: string;
  };
}
